import React, {
  useState,
  useEffect,
  useRef,
  CSSProperties,
  ReactElement,
  useCallback,
} from "react";
import { Tree, NodeRendererProps } from "react-arborist";
import {
  Text,
  Card,
  BlockStack,
  InlineStack,
  Checkbox,
} from "@shopify/polaris";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchCategories,
  setDestroy,
} from "containers/document/category-permissions-tree/category-tree-permission.Slice";
import { RootState, AppDispatch } from "configureStore";
import "../categoryTree.css";
import { Category, CategoryDataTbl } from "types/Document";
import useResizeObserver from "use-resize-observer";
import Loading from "components/loading/Loading";
import { assignFileCategoryPermissionToUser } from "services/files-category.service";
import useIsBelowLg from "containers/document/use-is-below-lg";

// The two checkbox columns of the touch layout share one width, so the column
// titles above the tree line up with the checkboxes in every row.
const CHECKBOX_COLUMN = "flex w-[4.5rem] shrink-0 justify-center";

const FIlesCategoryTree = () => {
  const { t } = useTranslation();
  const [activeNodeId, setActiveNodeId] = useState<string>("");
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>(
    {}
  );
  const { categoriesData, isLoading } = useSelector(
    (state: RootState) => state.documentCategoryPermissionReducer
  );
  const { isUserMaster } = useSelector(
    (state: RootState) => state.companySelectReducer
  );

  const { ref, height } = useResizeObserver();
  const isBelowLg = useIsBelowLg();

  const dispatch: AppDispatch = useDispatch();
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();

  const loadFilesCategories = useCallback(() => {
    if (companyId && userId) {
      dispatch(fetchCategories({ companyId, userId }));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    loadFilesCategories();
    setActiveNodeId("");

    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, loadFilesCategories]);

  function transformData(data: Category[]): CategoryDataTbl[] {
    return data
      ?.map((item) => ({
        id: item.id,
        name: item.title,
        count: item.fileCount,
        parentId: item.parentCategory,
        children: item.subcategories ? transformData(item.subcategories) : [],
        order: item.order,
        isPermission: item.isPermission,
        allChildCheck: item.allChildCheck,
        childCategoryCount: item.childCategoryCount,
      }))
      .sort((a, b) => (Number(a.order) ?? 0) - (Number(b.order) ?? 0));
  }

  const transformedData: CategoryDataTbl[] = transformData(categoriesData);

  const handleClick = (node: any) => {
    setActiveNodeId(node.id);
    toggleNode(node.id);
  };

  // Toggle the expanded state of a node
  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const handleToggle = () => {
    // console.log(node, "node");
  };

  const nodeRef = useRef<HTMLDivElement>(null);

  const handlePermissionCategory = async (id: string, selectallchild: any) => {
    const payload = {
      user: userId,
      category: id,
      selectallchild,
    };
    await assignFileCategoryPermissionToUser(companyId, payload);
    if (companyId && userId) {
      dispatch(fetchCategories({ companyId, userId }));
    }
  };

  const renderNode = ({
    node,
    style,
    dragHandle,
  }: NodeRendererProps<CategoryDataTbl>): ReactElement => {
    const isActive = node.id === activeNodeId;
    const isExpanded = expandedNodes[node.id] || false; // Check if node is expanded from localStorage

    // Ensure node is opened if it should be expanded
    if (isExpanded && !node.isOpen) {
      // Use requestAnimationFrame to avoid calling during render
      requestAnimationFrame(() => {
        if (node && !node.isOpen) {
          node.toggle(); // Ensure node is opened
        }
      });
    }

    if (isBelowLg) {
      return (
        <div
          style={style as CSSProperties}
          tabIndex={0}
          onClick={() => handleClick(node)}
          className={
            isActive ? "node_txt active-node flex items-center" : "node_txt flex items-center"
          }
        >
          {node.children!.length > 0 ? (
            <button
              type="button"
              className="flex h-10 w-10 shrink-0 items-center justify-center"
              aria-label={node.isOpen ? t("collapse") : t("expand")}
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.id);
                node.toggle();
              }}
            >
              {node.isOpen ? (
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          ) : (
            <span className="w-10 shrink-0" />
          )}
          <span className="min-w-0 flex-1 truncate" title={node.data.name}>
            {node.data.name}
          </span>
          <span className="ml-1 shrink-0 text-sm text-gray-500">
            [{node.data.count <= 0 ? "x" : node.data.count}]
          </span>
          <span
            className={CHECKBOX_COLUMN}
            onClick={(e) => {
              e.stopPropagation();
              handlePermissionCategory(node.id, "");
            }}
          >
            <Checkbox
              label="checkbox"
              labelHidden
              checked={node.data.isPermission}
            />
          </span>
          <span
            className={CHECKBOX_COLUMN}
            onClick={(e) => {
              e.stopPropagation();
              if (node.data.childCategoryCount) {
                handlePermissionCategory(node.id, node.data.allChildCheck);
              }
            }}
          >
            {node.data.childCategoryCount ? (
              <Checkbox
                label="checkbox"
                labelHidden
                checked={node.data.allChildCheck}
              />
            ) : null}
          </span>
        </div>
      );
    }

    return (
      <div
        style={style as CSSProperties}
        ref={nodeRef}
        tabIndex={0}
        onClick={() => handleClick(node)}
        className={isActive ? "node_txt active-node" : "node_txt"}
      >
        <InlineStack gap="100">
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {node.children!.length! > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.id);
                  node.toggle();
                }}
                style={{ marginRight: "8px" }}
              >
                {node.isOpen ? "-" : "+"}
              </button>
            ) : (
              <button
                disabled
                className="disabled_btn"
                style={{ marginRight: "8px" }}
              ></button>
            )}
            <span
              style={{
                display: node.data.name.length ? "inline-block" : "none",
                whiteSpace: "nowrap",
              }}
              title={node.data.name}
            >
              {node.data.name}
            </span>
            &nbsp;[{node.data.count <= 0 ? "x" : node.data.count}]
            <>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handlePermissionCategory(node.id, "");
                }}
                style={{ marginLeft: "8px" }}
              >
                <Checkbox
                  label="checkbox"
                  labelHidden
                  checked={node.data.isPermission}
                />
              </span>
              {(node.data?.childCategoryCount || 0 > 0) && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePermissionCategory(node.id, node.data.allChildCheck);
                  }}
                  style={{ marginLeft: "8px" }}
                >
                  <Checkbox
                    label="checkbox"
                    labelHidden
                    checked={node.data.allChildCheck}
                  />
                </span>
              )}
            </>
          </span>
        </InlineStack>
      </div>
    );
  };

  return (
    <BlockStack gap="500">
      <Card roundedAbove="sm">
        {/* The title and the column headings sit outside the measured box, so the
            tree gets exactly the box's height and its last rows are not pushed
            out of view. */}
        <div className="spinner_cus_main">
          <Text as="h2" variant="headingSm">
            {t("category")}
          </Text>

          {isLoading && (
            <span className="spinner_cus">
              <Loading size={"small"} />
            </span>
          )}
        </div>
        {isBelowLg && (
          <div className="flex items-end border-b border-gray-200 pb-1 text-center text-[11px] leading-tight text-gray-500">
            <span className="flex-1" />
            <span className={CHECKBOX_COLUMN}>{t("access")}</span>
            <span className={CHECKBOX_COLUMN}>
              {t("category_access_subcategories")}
            </span>
          </div>
        )}
        <div className="arborist-container" ref={ref}>
          <Tree
            data={transformedData}
            onToggle={handleToggle}
            disableMultiSelection
            idAccessor="id"
            openByDefault={false}
            rowHeight={isBelowLg ? 44 : 30}
            height={height}
            // width={width}
          >
            {renderNode}
          </Tree>
        </div>
      </Card>
    </BlockStack>
  );
};

export default FIlesCategoryTree;
